import DOMPurify from 'dompurify';

/**
 * Sanitizes markdown content to remove dangerous HTML tags like script, iframe with javascript:, etc.
 * This prevents XSS attacks while preserving safe HTML elements.
 */
export function sanitizeMarkdownContent(content: string): string {
  if (!content) return content;

  // First, remove script tags and dangerous event handlers from the raw content
  let sanitized = content
    // Remove script tags completely
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove javascript: protocols
    .replace(/javascript:/gi, '')
    // Remove on* event handlers (onclick, onerror, etc.)
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove iframe srcdoc attributes (can execute scripts)
    .replace(/<iframe[^>]*srcdoc\s*=\s*["'][^"']*["'][^>]*>/gi, '<iframe>')
    .replace(/<iframe[^>]*srcdoc\s*=\s*[^\s>]*[^>]*>/gi, '<iframe>')
    // Remove encoded script tags in srcdoc (HTML entities)
    .replace(/srcdoc\s*=\s*["'][^"']*&#x3C;[^"']*script[^"']*["']/gi, '')
    .replace(/srcdoc\s*=\s*["'][^"']*&lt;[^"']*script[^"']*["']/gi, '');

  // Use DOMPurify to sanitize HTML content
  // Allow safe HTML tags but remove script, iframe with javascript:, etc.
  const config = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'video', 'source',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span', 'iframe'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'width', 'height', 'controls', 'type',
      'class', 'id', 'style', 'target', 'rel', 'frameborder', 'allow', 'allowfullscreen'
    ],
    ALLOW_DATA_ATTR: false,
    // Remove iframes with javascript: or data: protocols
    FORBID_TAGS: ['script', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur', 'srcdoc'],
  };

  // Sanitize HTML content
  sanitized = DOMPurify.sanitize(sanitized, config);

  return sanitized;
}

/**
 * Validates if content contains dangerous patterns
 */
export function hasDangerousContent(content: string): boolean {
  if (!content) return false;

  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /\s*on\w+\s*=\s*["'][^"']*["']/gi,
    /<iframe[^>]*src\s*=\s*["']javascript:/gi,
    /<iframe[^>]*src\s*=\s*["']data:/gi,
    // Check for srcdoc attribute (can execute scripts)
    /<iframe[^>]*srcdoc\s*=/gi,
    // Check for encoded script tags in srcdoc (HTML entities)
    /srcdoc\s*=\s*["'][^"']*&#x3C;[^"']*script/gi,
    /srcdoc\s*=\s*["'][^"']*&lt;[^"']*script/gi,
    // Check for script content in srcdoc (decoded)
    /srcdoc\s*=\s*["'][^"']*<script/gi,
  ];

  return dangerousPatterns.some(pattern => pattern.test(content));
}

/**
 * Sanitizes error messages to remove technical details and format them nicely
 */
export function sanitizeErrorMessage(error: unknown): string {
  if (!error) return 'An unexpected error occurred';

  let errorMessage = '';
  
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    errorMessage = String(error);
  }

  // Check for specific technical errors that should be replaced with user-friendly messages
  const technicalErrorPatterns = [
    /randomBytes/i,
    /randombytes/i,
    /mc\.randomBytes/i,
    /mc\.randombytes/i,
    /crypto\.randomBytes/i,
    /\.randomBytes/i,
  ];

  // If error contains technical crypto/randomBytes references, replace with friendly message
  if (technicalErrorPatterns.some(pattern => pattern.test(errorMessage))) {
    return 'Unable to save content. Please check your input and try again.';
  }

  // Remove common technical error prefixes
  errorMessage = errorMessage
    .replace(/^Error:\s*/i, '')
    .replace(/^Failed to\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();

  // If the message is empty or too technical, provide a user-friendly message
  if (!errorMessage || errorMessage.length < 3) {
    return 'Unable to save content. Please check your input and try again.';
  }

  // Capitalize first letter
  return errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
}

